import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  keyframes,
} from "@chakra-ui/react";
import { PollResults, QuizResults } from "@components/ActivityTypes";
import RemixPollIcon from "@components/Icons/RemixPollIcon";
import RemixQuizIcon from "@components/Icons/RemixQuizIcon";
import RemixAnnouncementIcon from "@components/Icons/RemixAnnouncementIcon";

const getActivityTypeConfig = (activity) => {
  const type = activity?.type;
  switch (type) {
    case "poll":
      return { label: "Poll:", color: "#E5A100", Icon: RemixPollIcon };
    case "quiz":
      return { label: "Quiz:", color: "#5DCDFD", Icon: RemixQuizIcon };
    case "share":
      return activity?.link
        ? {
            label: "Link Share:",
            color: "#32D583",
            Icon: RemixAnnouncementIcon,
          }
        : {
            label: "Announcement:",
            color: "#4C47CC",
            Icon: RemixAnnouncementIcon,
          };
    default:
      return {
        label: "Announcement:",
        color: "#4C47CC",
        Icon: RemixAnnouncementIcon,
      };
  }
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

/**
 * DemoResultsModal — centered modal showing actual poll results
 * with commentary after a demo activity ends.
 */
const DemoResultsModal = ({
  isOpen,
  stepConfig,
  activity,
  onAction,
  onEndDemo,
}) => {
  if (!isOpen || !stepConfig || stepConfig.modalType !== "results_modal")
    return null;

  const hasResults =
    activity && activity.options?.length > 0 && activity.response?.length > 0;

  // Choose the right results component based on activity type
  const ResultComponent = activity?.type === "quiz" ? QuizResults : PollResults;

  const typeConfig = activity ? getActivityTypeConfig(activity) : null;
  const TypeIcon = typeConfig?.Icon;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onEndDemo}
      isCentered
      size="md"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent
        borderRadius="16px"
        mx={4}
        fontFamily="Geist, sans-serif"
        py={4}
        px={2}
        maxH="85vh"
      >
        <ModalBody overflowY="auto">
          <VStack spacing={3} align="stretch">
            {/* Activity type header — mirrors remix modal */}
            {activity && typeConfig && (
              <VStack spacing={1} align="stretch">
                <HStack spacing={2} align="center">
                  <Box>
                    <TypeIcon />
                  </Box>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    lineHeight="1.33"
                    letterSpacing="-0.04em"
                    color={typeConfig.color}
                    fontFamily="Geist, sans-serif"
                  >
                    {typeConfig.label}
                  </Text>
                </HStack>
                {activity.title && (
                  <Text
                    fontSize="md"
                    fontWeight="700"
                    lineHeight="1.33"
                    letterSpacing="-0.04em"
                    color="#0C001C"
                    fontFamily="Geist, sans-serif"
                  >
                    {activity.title}
                  </Text>
                )}
              </VStack>
            )}

            {/* Heading */}
            {stepConfig.heading && (
              <Text
                fontSize="md"
                fontWeight="700"
                color="#0C001C"
                textAlign="center"
              >
                {stepConfig.heading}
              </Text>
            )}

            {/* Poll Results — title omitted since it's shown in the header above */}
            {hasResults && (
              <Box
                bg="gray.50"
                borderRadius="12px"
                p={3}
                animation={`${fadeInUp} 0.4s ease-out`}
              >
                <ResultComponent
                  options={activity.options}
                  response={activity.response}
                />
              </Box>
            )}

            {/* Commentary body */}
            {stepConfig.body && (
              <Text
                fontSize="sm"
                color="#6B7280"
                lineHeight="1.6"
                textAlign="center"
              >
                {stepConfig.body}
              </Text>
            )}

            {/* Buttons */}
            {stepConfig.buttons?.length > 0 && (
              <VStack spacing={2} w="100%" pt={1}>
                {stepConfig.buttons.map((btn) => (
                  <Button
                    key={btn.action}
                    w="100%"
                    bg={btn.variant === "primary" ? "#4C47CC" : "transparent"}
                    color={btn.variant === "primary" ? "white" : "#6B7280"}
                    fontWeight="700"
                    fontSize="sm"
                    borderRadius="8px"
                    py={3}
                    border={btn.variant === "secondary" ? "1px solid" : "none"}
                    borderColor={
                      btn.variant === "secondary" ? "gray.300" : "transparent"
                    }
                    _hover={{
                      bg: btn.variant === "primary" ? "#3A37B3" : "#F3F4F6",
                    }}
                    onClick={() => onAction(btn.action)}
                  >
                    {btn.label}
                  </Button>
                ))}
              </VStack>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default DemoResultsModal;
